import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import "./index.css";
import App from "./App";

// MSAL imports
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { msalConfig } from "./authConfig";

// PostHog analytics
import posthog from "posthog-js";

// Google Analytics 4
import { initGA, identifyGA } from "./utils/ga";
initGA();

// Dev-only query performance tracker
import { install as installQueryTracker } from "./utils/queryTracker";
installQueryTracker();

const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    session_recording: {
      recordCrossOriginIframes: true,
    },
    enable_recording_console_log: true,
  });
}

/**
 * Demo mode: skip MSAL initialization entirely when no client ID is configured.
 * This avoids the AADSTS900144 error ("request body must contain client_id").
 */
const isDemoMode =
  process.env.REACT_APP_DEMO_MODE === "true" ||
  !process.env.REACT_APP_CLIENT_ID;

// Only create a real MSAL instance when we have a client ID
export let msalInstance: IPublicClientApplication;

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

if (isDemoMode) {
  // Demo mode — render immediately without MSAL
  console.log("🎭 Running in Demo Mode (no Azure AD)");
  root.render(
    <Router>
      <App pca={null} />
    </Router>,
  );
} else {
  // Live mode — initialize MSAL then render
  const pca = new PublicClientApplication(msalConfig);
  msalInstance = pca;

  pca.initialize().then(() => {
    const accounts = pca.getAllAccounts();
    if (accounts.length > 0) {
      pca.setActiveAccount(accounts[0]);
    }

    pca.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        pca.setActiveAccount(account);

        // Identify user in PostHog on login
        if (posthogKey && account) {
          posthog.identify(account.localAccountId, {
            email: account.username,
            name: account.name,
            tenantId: account.tenantId,
          });
        }
        if (account) identifyGA(account.localAccountId);
      }
    });

    // Identify already-logged-in users on page load
    const activeAccount = pca.getActiveAccount();
    if (posthogKey && activeAccount) {
      posthog.identify(activeAccount.localAccountId, {
        email: activeAccount.username,
        name: activeAccount.name,
        tenantId: activeAccount.tenantId,
      });
    }
    if (activeAccount) identifyGA(activeAccount.localAccountId);

    root.render(
      <Router>
        <App pca={pca} />
      </Router>,
    );
  });
}
